// JSBridge.ts
import { isAndroid, parseJson } from '@/utils';
import type { EFBridgeProps } from '@/types/bridge';

// 初始化状态标志
let isInitialized = false;

/**
 * Android  与安卓交互时：
 *      1、不调用这个函数安卓无法调用 H5 注册的事件函数；
 *      2、但是 H5 可以正常调用安卓注册的事件函数；
 *      3、还必须在 setupWebViewJavascriptBridge 中执行 bridge.init 方法，否则：
 *          ①、安卓依然无法调用 H5 注册的事件函数
 *          ①、H5 正常调用安卓事件函数后的回调函数无法正常执行
 *
 * @param {*} callback
 */
const androidHandler = (callback: (bridge: any) => void) => {
    if (window.WebViewJavascriptBridge) {
        callback(window.WebViewJavascriptBridge);
    } else {
        document.addEventListener(
            'WebViewJavascriptBridgeReady',
            function () {
                callback(window.WebViewJavascriptBridge);
            },
            false,
        );
    }
};

/**
 * IOS 与 IOS 交互时，使用这个函数即可，别的操作都不需要执行
 * @param {*} callback
 */
const iosHandler = (callback: (bridge: any) => void) => {
    if (window.WebViewJavascriptBridge) {
        return callback(window.WebViewJavascriptBridge);
    }
    if (window.WVJBCallbacks) {
        return window.WVJBCallbacks.push(callback);
    }
    window.WVJBCallbacks = [callback];
    const WVJBIframe = document.createElement('iframe');
    WVJBIframe.style.display = 'none';
    WVJBIframe.src = 'https://__bridge_loaded__';
    document.documentElement.appendChild(WVJBIframe);
    setTimeout(function () {
        document.documentElement.removeChild(WVJBIframe);
    }, 0);
};

// 实现JSBridge类
const JSBridge: EFBridgeProps = {
    init: () => {
        if (typeof window !== 'undefined' && !isInitialized) {
            window.setupWebViewJavascriptBridge = isAndroid() ? androidHandler : iosHandler;
            if (isAndroid()) {
                /**
                 * 与安卓交互时，不调用这个函数会导致：
                 * 1、H5 可以正常调用 安卓这边的事件函数，但是无法再调用到 H5 的回调函数
                 *
                 * 前提 setupWebViewJavascriptBridge 这个函数使用的是 andoirFunction 这个，否则还是会导致上面 1 的现象出现
                 */
                window.setupWebViewJavascriptBridge((bridge: any) => {
                    // 注册 H5 界面的默认接收函数（与安卓交互时，不注册这个事件无法接收回调函数）
                    bridge.init((msg: any, responseCallback: any) => {
                        console.log('setupWebViewJavascriptBridge', msg);
                        if (responseCallback) {
                            console.log('setupWebViewJavascriptBridge-responseCallback');
                            responseCallback('h5 setup webview');
                        }
                    });
                    isInitialized = true; // 设置初始化状态为true
                });
            }
        }
    },
    // 注册回调函数，用于接收原生传递的数据
    registerHandler: (name: string, func: any) => {
        if (typeof window !== 'undefined' && window.setupWebViewJavascriptBridge) {
            window.setupWebViewJavascriptBridge((bridge: any) => {
                console.log('core->registerHandler->');
                bridge.registerHandler(name, (data: any) => {
                    console.log('registerHandler->data->', data);
                    const result = data ? parseJson(data) : null;
                    console.log('registerHandler->data->result->name', name);
                    try {
                        func && func(result);
                    } catch (err) {
                        console.log('registerHandler->data->err', name);
                        console.error(err);
                    }
                });
            });
        }
    },
    // 调用原生方法
    callHandler: (name: string, data: any, func: any) => {
        if (typeof window !== 'undefined' && window.setupWebViewJavascriptBridge) {
            window.setupWebViewJavascriptBridge((bridge: any) => {
                bridge.callHandler(name, data, (data: any) => {
                    console.log('callHandler->data', data);
                    func && func(parseJson(data));
                });
            });
        }
    },
};

export default JSBridge;
